/**
 * Client types + API for Tester Report Closeout.
 * Mirrors the server JSON shapes (camelCase) from src/server/services/reportInput*.
 */
import { api } from "@/lib/api";

export type ReportInputState = "none" | "tester_todo" | "city_hold" | "submitted" | "filed";

export interface TesterQueueItem {
    jobId: string;
    siteName: string | null;
    city: string | null;
    deviceLabel: string | null;
    serialNumber: string | null;
    platform: string | null;
    isPortal: boolean;
    accountNumber: string | null;
    reason: string | null;
    testAt: string | null;
    result: string | null;
}

export interface PortalCheatSheet {
    platform: string;
    steps: string[];
}

export interface ReportInputPacket extends TesterQueueItem {
    state: ReportInputState;
    cv1: string | null;
    cv2: string | null;
    testerName: string | null;
    testerLicense: string | null;
    portalUrl: string | null;
    deepLink: string | null;
    cheatSheet: PortalCheatSheet;
    snoozeCount: number;
    gateForced: boolean;
    confirmationNumber: string | null;
    photos: Array<{ id: string; fileName: string | null; slotLabel?: string | null }>;
}

export interface GateResponse {
    show: boolean;
    mode: "soft" | "hard";
    jobId: string | null;
    hoursSinceFlagged: number | null;
    packet: ReportInputPacket | null;
}

export interface OfficeWatchItem {
    jobId: string;
    siteName: string | null;
    testerName: string | null;
    state: ReportInputState;
    reason: string | null;
    snoozeCount: number;
    gateForced: boolean;
    lastSnoozeReason: string | null;
    testAt: string | null;
    submittedAt: string | null;
}

// ── Tester ──
export const getTesterQueue = () => api.get<{ items: TesterQueueItem[] }>("/api/report-input/queue");
export const getReportInputPacket = (jobId: string) =>
    api.get<{ packet: ReportInputPacket }>(`/api/report-input/jobs/${jobId}`);
export const getGate = () => api.get<GateResponse>("/api/report-input/gate");
export const markSubmitted = (jobId: string, confirmationNumber?: string) =>
    api.post(`/api/report-input/jobs/${jobId}/submitted`, { confirmationNumber: confirmationNumber || null });
export const snoozeReport = (jobId: string, reason: string) =>
    api.post(`/api/report-input/jobs/${jobId}/snooze`, { reason });
export const requestCityHold = (jobId: string, reason: string) =>
    api.post(`/api/report-input/jobs/${jobId}/request-city-hold`, { reason });
export const selfQueue = (jobId: string) => api.post(`/api/report-input/jobs/${jobId}/self-queue`, {});

// ── Office ──
export const getOfficeWatch = () => api.get<{ items: OfficeWatchItem[] }>("/api/report-input/office/queue");
export const flagWaitingOnTester = (jobId: string, reason: string, testerId?: string) =>
    api.post(`/api/report-input/jobs/${jobId}/flag-tester`, { reason, testerId: testerId ?? null });
export const officeCityHold = (jobId: string, reason: string) =>
    api.post(`/api/report-input/jobs/${jobId}/city-hold`, { reason });
export const revertToTester = (jobId: string, reason: string) =>
    api.post(`/api/report-input/jobs/${jobId}/revert`, { reason });
export const forceReport = (jobId: string) => api.post(`/api/report-input/jobs/${jobId}/force`, {});
export const reassignTester = (jobId: string, testerId: string) =>
    api.post(`/api/report-input/jobs/${jobId}/reassign`, { testerId });

/** Whole hours since a test ISO timestamp, for the "tested Nh ago" chips. */
export function hoursSince(iso: string | null): number | null {
    if (!iso) return null;
    return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 3_600_000));
}
